import {useCampaignInfluencerRatingMutation, useCampaignReportingApplicationsQuery} from "@/api/campaignApi";
import PendingContents from "@/components/Campaign/management/activeTab/PendingContents";
import AmbassadorshipSuggestion from "@/components/Campaign/management/reportingTab/AmbassadorshipSuggestion";
import ContentStatistics from "@/components/Campaign/management/reportingTab/ContentStatistics";
import InfluencerRatingSection from "@/components/Campaign/management/reportingTab/InfluencerRatingSection";
import RatingCommentInput from "@/components/Campaign/management/reportingTab/RatingCommentInput";
import ReportingContentCard from "@/components/Campaign/management/reportingTab/ReportingContentCard";
import Button from "@/components/Common/Button";
import Loader from "@/components/Common/kpi/Loader";
import Modal from "@/components/Common/Modal";
import ModalBreadcrumbs from "@/components/Common/ModalBreadcrumbs";
import AmbassadorActionModal from "@/components/Common/referral/AmbassadorActionModal";
import AmbassadorRequestModal from "@/components/Influencer/InfluencerDetail/AmbassadorRequestModal";
import InfluencerMiniProfile from "@/components/Influencer/InfluencerMiniProfile";
import {useInfluencerContent} from "@/hooks/useInfluencerContent";
import clsxm from "@/lib/clsxm";
import useCampaignManagementStore, {useRequiredCampaignContent} from "@/store/campaignManagement";
import {useCarouselStore} from "@/store/carousel";
import {AMBASSADORSHIP_SUBVIEW_TITLE_KEYS, MAX_RATING_VALUE} from "@/store/constants";
import {isRateInfluencersModalOpenAtom} from "@/store/jotaiAtoms";
import type {TAmbassadorshipRatingSubview, TInstagramContent} from "@/store/types";
import {useAtom} from "jotai";
import {countBy, isEmpty} from "lodash";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDraggable} from "react-use-draggable-scroll";
import {useImmer} from "use-immer";

type TRatingContent = TInstagramContent & {
  submissionStatus?: "PUBLISHED" | "REJECTED";
};

const RateInfluencersModal = () => {
  const {t} = useTranslation(["campaign_reporting", "common", "influencer_invite", "referral"]);
  const allRatingApplicationIds = useCampaignManagementStore(state => state.management.total_influencer_ratings);
  const campaign = useCampaignManagementStore(state => state.campaign);
  const ratedInfluencerApplicationIds = useCampaignManagementStore(
    state => state.management.completed_influencer_ratings,
  );
  const openCarousel = useCarouselStore(state => state.open);
  const setCarouselIndex = useCarouselStore(state => state.setCurrentIndex);
  const [isModalOpen, setShowModal] = useAtom(isRateInfluencersModalOpenAtom);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [loadingButton, setLoadingButton] = useState<"rate" | "max" | null>(null);
  const [subview, setSubview] = useState<TAmbassadorshipRatingSubview | null>(null);

  const divRef = useRef<HTMLDivElement>(null!);
  const {events} = useDraggable(divRef);
  const requiredContent = useRequiredCampaignContent();

  const unratedApplicationIds =
    allRatingApplicationIds?.filter(id => !ratedInfluencerApplicationIds?.includes(id)) ?? [];

  const currentUnratedIndex = unratedApplicationIds.findIndex(id => id === allRatingApplicationIds?.[currentIndex!]);
  const isOnlyUnrated = unratedApplicationIds.length === 1;

  const {
    data: reportingApplications,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useCampaignReportingApplicationsQuery({searchParam: ""});

  const currentApplication = reportingApplications?.[currentIndex!];

  const [ratingState, setRatingState] = useImmer({
    comment: currentApplication?.influencer_rating?.comment ?? "",
    reliability: currentApplication?.influencer_rating?.reliability ?? 0,
    contentQuality: currentApplication?.influencer_rating?.quality ?? 0,
  });

  const {posts, reels, stories, tiktokContent, ugcContent, user} = useInfluencerContent(currentApplication);

  const rejectedContent: TRatingContent[] = currentApplication?.rejected_submissions
    ? currentApplication.rejected_submissions.map(submission => ({
        applicationId: currentApplication.id,
        id: submission.id,
        image: submission.thumbnail_file!,
        instagram_media: {
          created: submission.created,
          id: submission.id,
          media_file: submission.media_file,
          media_type: submission.media_type ?? "IMAGE",
          media_product_type: submission.content_type === "TIKTOK_VIDEO" ? "TIKTOK" : submission.content_type,
          thumbnail_file: submission.thumbnail_file,
        },
        submissionStatus: "REJECTED",
        user,
      }))
    : [];

  const allContent: TRatingContent[] = [
    ...posts,
    ...reels,
    ...stories,
    ...tiktokContent,
    ...ugcContent,
    ...rejectedContent,
  ];

  const adminMarkedCompletedContent = () => {
    const rejectedCounts = {
      POST: 0,
      REEL: 0,
      STORY: 0,
      TIKTOK_VIDEO: 0,
      UGC: 0,
      ...countBy(currentApplication?.rejected_submissions, "content_type"),
    };

    const publishedPosts = currentApplication?.posts_done || 0;
    const publishedReels = currentApplication?.reels_done || 0;
    const publishedStories = currentApplication?.stories_done || 0;
    const publishedTiktok = currentApplication?.tiktok?.length || 0;
    const publishedUgc = currentApplication?.ugc_done || 0;

    return [
      {
        type: "POST" as const,
        count: Math.max(0, requiredContent.instagram.posts - publishedPosts - rejectedCounts.POST),
      },
      {
        type: "REEL" as const,
        count: Math.max(0, requiredContent.instagram.reels - publishedReels - rejectedCounts.REEL),
      },
      {
        type: "STORIES" as const,
        count: Math.max(0, requiredContent.instagram.stories - publishedStories - rejectedCounts.STORY),
      },
      {
        type: "TIKTOK_VIDEO" as const,
        count: Math.max(0, requiredContent.tiktok - publishedTiktok - rejectedCounts.TIKTOK_VIDEO),
      },
      {type: "UGC" as const, count: Math.max(0, requiredContent.ugc - publishedUgc - rejectedCounts.UGC)},
    ].filter(item => item.count > 0);
  };

  const {mutate: rateInfluencer, isPending} = useCampaignInfluencerRatingMutation({
    applicationId: currentApplication?.id,
    onSuccess: () => {
      setLoadingButton(null);
      if (!isOnlyUnrated) {
        const currentId = allRatingApplicationIds?.[currentIndex!];
        const nextUnratedId =
          unratedApplicationIds[currentUnratedIndex + 1] ?? unratedApplicationIds.find(id => id !== currentId);

        if (nextUnratedId !== undefined && allRatingApplicationIds) {
          const nextIndex = allRatingApplicationIds.findIndex(id => id === nextUnratedId);
          if (nextIndex !== -1) {
            setCurrentIndex(nextIndex);
          }
        }
      } else {
        setShowModal(false);
      }
    },
    onError: () => setLoadingButton(null),
    rating: currentApplication?.influencer_rating,
    userId: currentApplication?.influencer.user.id,
  });

  useEffect(() => {
    if (!allRatingApplicationIds?.length || !hasNextPage || currentIndex === null) {
      return;
    }

    const currentId = allRatingApplicationIds[currentIndex];
    const isSelectedFetched = reportingApplications?.some(application => application.id === currentId);

    const nextUnratedId =
      unratedApplicationIds[currentUnratedIndex + 1] ?? unratedApplicationIds.find(id => id !== currentId);
    const isNextUnratedFetched = nextUnratedId
      ? reportingApplications?.some(application => application.id === nextUnratedId)
      : true;

    if (!isSelectedFetched || !isNextUnratedFetched) {
      fetchNextPage();
    }
  }, [
    allRatingApplicationIds,
    currentIndex,
    currentUnratedIndex,
    fetchNextPage,
    hasNextPage,
    reportingApplications,
    unratedApplicationIds,
  ]);

  useEffect(() => {
    if (isModalOpen && allRatingApplicationIds?.length) {
      const firstUnratedId = unratedApplicationIds[0];

      if (firstUnratedId !== undefined) {
        const firstUnratedIndex = allRatingApplicationIds.findIndex(id => id === firstUnratedId);

        if (firstUnratedIndex !== -1) {
          setCurrentIndex(firstUnratedIndex);
        }
      }
    }
  }, [isModalOpen, allRatingApplicationIds]);

  useEffect(() => {
    if (currentApplication) {
      setRatingState({
        comment: currentApplication.influencer_rating?.comment ?? "",
        reliability: currentApplication.influencer_rating?.reliability ?? 0,
        contentQuality: currentApplication.influencer_rating?.quality ?? 0,
      });
    }
  }, [currentApplication]);

  if (currentIndex === null) {
    return <div ref={divRef} className="hidden" />;
  }

  return (
    <Modal
      backgroundClassName="max-w-207.5"
      className={subview ? "p-0" : undefined}
      customTitleRow={
        subview && (
          <ModalBreadcrumbs
            crumbs={[{label: t("rate_influencers_title")}, {label: t(AMBASSADORSHIP_SUBVIEW_TITLE_KEYS[subview.type])}]}
            onBack={() => setSubview(null)}
          />
        )
      }
      isLoading={isPending}
      modalTitle={t("rate_influencers_title")}
      onClose={subview ? () => setSubview(null) : () => setShowModal(false)}
      shouldHideBottomRow={!!subview}
      customBottomRow={
        <div className="border-neutral-white-dark flex shrink-0 grow gap-3 border-t p-5">
          <Button
            buttonStyle="White"
            className="flex-1"
            isDisabled={loadingButton === "rate"}
            isLoading={loadingButton === "max"}
            onClick={() => {
              setLoadingButton("max");
              rateInfluencer({
                comment: ratingState.comment,
                reliability: MAX_RATING_VALUE,
                contentQuality: MAX_RATING_VALUE,
              });
            }}>
            {t("give_max_stars", {max: MAX_RATING_VALUE})}
          </Button>
          <Button
            buttonStyle="Gradient"
            className="flex-1"
            isDisabled={!ratingState.contentQuality || !ratingState.reliability || loadingButton === "max"}
            isLoading={loadingButton === "rate"}
            onClick={() => {
              setLoadingButton("rate");
              rateInfluencer(ratingState);
            }}>
            {t(isOnlyUnrated ? "finish" : "common:rate")}
          </Button>
        </div>
      }>
      {subview?.type === "request" ? (
        <AmbassadorRequestModal data={subview.data} onClose={() => setSubview(null)} shouldRenderInline />
      ) : subview ? (
        <AmbassadorActionModal data={subview.data} onClose={() => setSubview(null)} shouldRenderInline />
      ) : (
        <>
          <span className="text-primary-dark-solid bg-primary-light-solid rounded-md px-5 py-3 text-center text-sm font-semibold">
            {t("rate_influencers_description")}
          </span>
          <div className="flex flex-col items-center justify-center gap-5">
            <div className="flex flex-col items-center gap-2">
              <span className="text-black-bluetint text-xl leading-5 font-semibold">
                {currentIndex + 1}/{allRatingApplicationIds?.length}
              </span>
              <div className="flex max-w-full flex-wrap justify-center gap-3">
                {allRatingApplicationIds?.map((id, index) => {
                  const isRated = ratedInfluencerApplicationIds?.includes(id);
                  const isSelected = index === currentIndex;

                  return (
                    <button
                      key={index}
                      className={clsxm(
                        "bg-primary-light-solid border-neutral-grey-light-dark size-3 shrink-0 rounded-full border",
                        isRated && "bg-success-herbal border-success-light-green cursor-default",
                        isSelected && "bg-primary-dark-solid border-primary-medium-solid",
                      )}
                      onClick={() => {
                        if (!isRated) {
                          setCurrentIndex(index);
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>
            {isLoading || !currentApplication ? (
              <Loader
                styles={{containerClassName: "h-136 flex items-center justify-center", loaderClassName: "size-12"}}
              />
            ) : (
              <>
                <div ref={divRef} {...events} className="w-full overflow-x-auto">
                  <div className="flex min-w-max flex-col gap-3">
                    <InfluencerMiniProfile
                      id={user.influencer_id}
                      image={user.picture}
                      firstName={user.first_name}
                      username={user.username}
                    />
                    <div className="flex space-x-3 pb-2 select-none">
                      {allContent.map((content, index) => (
                        <div
                          key={content.id}
                          onClick={() => {
                            setCarouselIndex(index);

                            if (!isEmpty(content)) {
                              openCarousel({
                                campaign: {id: campaign.id, title: campaign.title!},
                                initialMediaId: content.id,
                                media: allContent,
                                shouldUsePersistedIndex: true,
                              });
                            }
                          }}
                          className="relative flex max-w-44 cursor-pointer flex-col gap-3">
                          {content.instagram_media && (
                            <>
                              <ReportingContentCard
                                data={{
                                  ...content.instagram_media,
                                  image: content.image,
                                  status: content.submissionStatus,
                                }}
                              />
                              <ContentStatistics data={content.instagram_media} />
                            </>
                          )}
                        </div>
                      ))}
                      {currentApplication?.manual_task_completed &&
                        !!currentApplication.task_completed_on &&
                        adminMarkedCompletedContent().map(({type, count}) => (
                          <PendingContents
                            key={type}
                            count={count}
                            taskCompletedDate={currentApplication.task_completed_on ?? undefined}
                            type={type}
                          />
                        ))}
                    </div>
                  </div>
                </div>
                <div className="flex w-full flex-col gap-5 lg:flex-row lg:items-start">
                  <InfluencerRatingSection
                    className="shrink-0"
                    contentQuality={ratingState.contentQuality}
                    firstName={currentApplication.influencer.user.first_name}
                    onRateQuality={(value: number) =>
                      setRatingState(draft => {
                        draft.contentQuality = value;
                      })
                    }
                    onRateReliability={(value: number) =>
                      setRatingState(draft => {
                        draft.reliability = value;
                      })
                    }
                    reliability={ratingState.reliability}
                    textClassName="text-lg"
                  />
                  <AmbassadorshipSuggestion
                    className="lg:min-w-0 lg:flex-1"
                    contentQuality={ratingState.contentQuality}
                    influencer={currentApplication.influencer}
                    onSelectSubview={setSubview}
                    reliability={ratingState.reliability}
                  />
                </div>
                <RatingCommentInput
                  comment={ratingState.comment}
                  firstName={currentApplication.influencer.user.first_name}
                  onChange={(comment: string) =>
                    setRatingState(draft => {
                      draft.comment = comment;
                    })
                  }
                />
              </>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default RateInfluencersModal;
